import { FC, useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Box } from "@mui/material"
import { useOktaAuth } from "@okta/okta-react"

import { FormInput, UButton, UText } from "../../../components"
import { useAppDispatch } from "../../../core/app/hooks"
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../../core/app/slices/user/userSlice"
import { useMyNavigation } from "../../../hooks/useMyNavigation"
import ErrorAlert from "../components/ErrorAlert"
import PasswordInput from "../components/PasswordInput"
import PasswordIcon from "../Login/PasswordIcon"

interface IChangePasswordForm {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

interface OktaError {
  errorCode?: string
  errorSummary?: string
  errorCauses?: Array<{ errorSummary?: string }>
}

const ChangePassword: FC = () => {
  const { t } = useTranslation("common")
  const { oktaAuth } = useOktaAuth()
  const dispatch = useAppDispatch()
  const navigate = useMyNavigation()
  const [searchParams] = useSearchParams()

  const [stateToken, setStateToken] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>("")
  const [visibleOldPassword, setVisibleOldPassword] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
    setValue,
  } = useForm<IChangePasswordForm>({ mode: "onSubmit" })

  const oldPasswordValue = watch("oldPassword", "")

  useEffect(() => {
    // Get stateToken from URL params (passed from Login component)
    const token = searchParams.get("stateToken")
    if (token && !stateToken) {
      // Only process if we haven't already captured the token
      setStateToken(token)
      // Clear the stateToken from URL for security (prevent it from appearing in browser history, logs, etc.)
      navigate("/auth/changePassword", { replace: true })
    } else if (!token && !stateToken) {
      // If no stateToken in URL and not in state, redirect to login
      dispatch(setErrorMessage(t("auth.changePassword.invalidToken")))
      navigate("/auth/login")
    }
  }, [searchParams, stateToken, dispatch, navigate, t])

  const onSubmit: SubmitHandler<IChangePasswordForm> = async (data) => {
    setErrorMsg("")

    if (!stateToken) {
      setErrorMsg(t("auth.changePassword.invalidToken"))
      return
    }

    setIsSubmitting(true)

    try {
      const tx = await oktaAuth.tx.resume({ stateToken })
      // Use Okta's changePassword method with the stateToken
      const transaction = await tx.changePassword({
        stateToken: stateToken,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      })

      if (transaction.status === "SUCCESS") {
        dispatch(setSuccessMessage(t("auth.changePassword.passwordChangeSuccess")))
        navigate("/auth/login")
      } else {
        setErrorMsg(t("auth.changePassword.passwordChangeFailed"))
      }
    } catch (error: any) {
      const oktaError = error as OktaError
      // E0000014 is the error code for only password related errors that user should know.
      const firstErrorCause = oktaError?.errorCauses?.[0]
      const hasPasswordError = oktaError?.errorCode === "E0000014" && firstErrorCause?.errorSummary
      
      if (hasPasswordError) {
        setErrorMsg(firstErrorCause.errorSummary)
      } else {
        // If not a password related error, fallback to default error message
        setErrorMsg(t("auth.changePassword.passwordChangeFailed"))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {stateToken && (
        <>
          {errorMsg && <ErrorAlert message={errorMsg} />}
          <UText
            variant={"h5"}
            color={"text.primary"}
            sxProp={{
              textAlign: "center",
              padding: "16px 0px",
            }}
          >
            {t("auth.changePassword.title")}
          </UText>
          <UText
            variant={"body2"}
            color={"text.secondary"}
            sxProp={{
              textAlign: "center",
              padding: "0px 0px 16px 0px",
            }}
          >
            {t("auth.changePassword.subtitle")}
          </UText>
          <Box
            component={"form"}
            data-testid={"change-password-form"}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <UText variant={"h6"} color={"text.secondary"} component={"div"}>
                {t("auth.changePassword.currentPasswordLabel")}
              </UText>
              <FormInput
                fieldName={"oldPassword"}
                fieldType={visibleOldPassword ? "text" : "password"}
                rules={{
                  required: "formfieldErrors.passwordrequired",
                }}
                errors={errors}
                control={control}
                formSxprops={{
                  m: 0,
                }}
                endAdornmentIcon={
                  oldPasswordValue && (
                    <PasswordIcon
                      updatePasswordVisible={setVisibleOldPassword}
                      isError={!!(errors && errors.oldPassword)}
                    />
                  )
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <UText variant={"h6"} color={"text.secondary"}>
                {t("auth.changePassword.newPasswordSubtitle")}
              </UText>
              <PasswordInput
                setError={setError}
                clearErrors={clearErrors}
                setValue={setValue}
                formKey={"reset-form"}
                control={control}
                errors={errors}
                watch={watch}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <UButton
                sxProp={{ width: "100%" }}
                variant={"outlined"}
                btnType={"button"}
                btnText={t("auth.changePassword.cancelBtn")}
                onClickHandler={() => navigate("/auth/login")}
                disabled={isSubmitting}
                data-testid="cancel-button"
              />
              <UButton
                sxProp={{ width: "100%" }}
                variant={"contained"}
                btnType={"submit"}
                btnText={t("auth.changePassword.changePasswordBtn")}
                disabled={isSubmitting}
                data-testid="submit-button"
              />
            </Box>
          </Box>
        </>
      )}
    </>
  )
}

export default ChangePassword
